import type { RejectionReason, UploadOutcome } from "../../api"
import type { AttachmentRef } from "../../types"

/**
 * Where an image picked in the composer stands. `failed` is an error that is
 * not an upload outcome (the session dropped, say); like `unavailable`, it is
 * worth trying again.
 */
export type ImageDraft =
  | { phase: "uploading"; progress: number }
  | { phase: "clean"; attachment: AttachmentRef }
  | { phase: "rejected"; reason: RejectionReason }
  | { phase: "unavailable" }
  | { phase: "rate_limited"; retryAfter: number }
  | { phase: "failed" }

export function draftFromOutcome(outcome: UploadOutcome): ImageDraft {
  switch (outcome.status) {
    case "clean":
      return { phase: "clean", attachment: outcome.attachment }
    case "rejected":
      return { phase: "rejected", reason: outcome.reason }
    case "unavailable":
      return { phase: "unavailable" }
    case "rate_limited":
      return { phase: "rate_limited", retryAfter: outcome.retryAfter }
  }
}

const REJECTION_KEYS = {
  type: "chat.IMAGE_REJECTED_TYPE",
  size: "chat.IMAGE_REJECTED_SIZE",
  infected: "chat.IMAGE_REJECTED_INFECTED",
  undecodable: "chat.IMAGE_REJECTED_UNDECODABLE",
  not_allowed: "chat.IMAGE_NOT_ALLOWED",
  other: "chat.IMAGE_REJECTED_OTHER",
} as const

export type UploadNote = {
  key: string
  /** For the plural: minutes to wait. */
  count?: number
  canRetry?: boolean
}

/** What the composer tells the user about the image, if anything. */
export function uploadNote(draft: ImageDraft): UploadNote | undefined {
  switch (draft.phase) {
    case "uploading":
    case "clean":
      return undefined
    case "rejected":
      return { key: REJECTION_KEYS[draft.reason] }
    case "unavailable":
    case "failed":
      return { key: "chat.IMAGE_UPLOAD_FAILED", canRetry: true }
    case "rate_limited":
      return {
        key: "chat.IMAGE_RATE_LIMITED",
        count: Math.ceil(draft.retryAfter / 60),
      }
  }
}
